import type { ReactNode } from "react";
import { Badge, type BadgeVariant } from "./Badge";
import { Logo } from "./Logo";
import { UiLink } from "./link";
import { cn, focusRing } from "./utils";

export type NavItem = {
  id: string;
  label: string;
  href: string;
  icon: ReactNode;
  badge?: { count: number; variant?: BadgeVariant };
};

// Bottom tab bar on phones. The active tab sits on a blue tint pill.
export function TabBar({
  items,
  activeId,
  className,
}: {
  items: NavItem[];
  activeId?: string;
  className?: string;
}) {
  return (
    <nav
      aria-label="Main"
      className={cn(
        "box-border flex flex-none border-t border-line bg-linen px-2.5 pt-2 pb-[max(10px,env(safe-area-inset-bottom))]",
        className,
      )}
    >
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <UiLink
            key={item.id}
            href={item.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex min-h-[66px] flex-1 flex-col items-center gap-1 rounded-field text-xs no-underline",
              active ? "font-bold text-ink" : "font-medium text-ink-secondary",
              focusRing,
            )}
          >
            <span
              className={cn(
                "relative flex h-8 w-[62px] items-center justify-center rounded-full",
                active && "bg-blue-tint",
              )}
            >
              {item.icon}
              {item.badge && item.badge.count > 0 && (
                <Badge
                  variant={item.badge.variant ?? "unread"}
                  count={item.badge.count}
                  size="sm"
                  ringClassName="border-linen"
                  className="absolute -top-[5px] right-0.5"
                />
              )}
            </span>
            {item.label}
          </UiLink>
        );
      })}
    </nav>
  );
}

const barTone = { blue: "bg-blue", queravel: "bg-queravel" } as const;

/**
 * One row of the desktop rail. Its marker sits on the rail's left edge, centred on the tile: a tall
 * bar for the selected item, a short bar for unread, and a shorter grey one on hover when asked.
 */
export function RailSlot({
  active = false,
  unread = false,
  hoverMark = false,
  tone = "blue",
  className,
  children,
}: {
  active?: boolean;
  unread?: boolean;
  /** Shows a short grey bar while the pointer is over the row. Spaces use it. */
  hoverMark?: boolean;
  tone?: keyof typeof barTone;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("group/rail relative flex w-full justify-center", className)}>
      <span
        aria-hidden="true"
        data-rail-mark={active ? "selected" : unread ? "unread" : undefined}
        className={cn(
          "pointer-events-none absolute top-1/2 left-0 w-1 -translate-y-1/2 rounded-r-full transition-[height]",
          active
            ? cn("h-8", barTone[tone])
            : unread
              ? "h-2 bg-ink-secondary"
              : cn("h-0 bg-ink-muted", hoverMark && "group-hover/rail:h-3"),
        )}
      />
      {children}
    </div>
  );
}

// Left rail on desktop and web. Pages sit at the top, then a divider, then spaces.
export function NavRail({
  items,
  activeId,
  after,
  extra,
  bottom,
  homeHref = "/",
}: {
  items: NavItem[];
  activeId?: string;
  /** Shown in the top group after the page items, such as the Queravel tile. */
  after?: ReactNode;
  /** Shown under a divider, such as the spaces. */
  extra?: ReactNode;
  bottom?: ReactNode;
  homeHref?: string;
}) {
  return (
    <nav
      aria-label="Sections"
      className="box-border flex h-full w-[76px] flex-none flex-col items-center gap-2 border-r border-line bg-surface-header py-[18px]"
    >
      <UiLink href={homeHref} className={cn("mb-3.5 rounded-field", focusRing)} aria-label="Rhema home">
        <Logo size={38} title="Rhema" />
      </UiLink>
      {items.map((item) => {
        const active = item.id === activeId;
        return (
          <RailSlot key={item.id} active={active}>
            <UiLink
              href={item.href}
              aria-label={item.label}
              aria-current={active ? "page" : undefined}
              title={item.label}
              className={cn(
                "relative flex h-12 w-12 items-center justify-center rounded-tile",
                active ? "bg-surface text-blue" : "text-ink-secondary hover:bg-line",
                focusRing,
              )}
            >
              {item.icon}
              {item.badge && item.badge.count > 0 && (
                <Badge
                  variant={item.badge.variant ?? "unread"}
                  count={item.badge.count}
                  size="sm"
                  ringClassName="border-surface-header"
                  className="absolute -top-1 -right-1"
                />
              )}
            </UiLink>
          </RailSlot>
        );
      })}
      {after}
      {extra && (
        <>
          <div className="my-1.5 h-px w-8 bg-rail-divider" />
          {extra}
        </>
      )}
      <div className="flex-1" />
      {bottom}
    </nav>
  );
}
